/**
 * Hands a set of cases to the simulator, opened on one. Every run shares one workbench (thread,
 * timeline, socket handler), so while a run is in flight nothing loads: the running case stays
 * on screen, and asking for another says so.
 */

import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { toast } from '@/components/ui/sonner';
import { isWaitingAtom } from '@/stores/websocket.store';
import { openSimulatorSessionAtom, simulatorSessionAtom, type SimulatorSession } from './simulator-session.store';

const activeCase = (session: Omit<SimulatorSession, 'id'> | null) =>
  session && session.activeIndex !== null ? (session.cases[session.activeIndex] ?? null) : null;

export const useOpenInSimulator = () => {
  const openSession = useSetAtom(openSimulatorSessionAtom);
  const session = useAtomValue(simulatorSessionAtom);
  const runInFlight = useAtomValue(isWaitingAtom);

  return useCallback(
    (handOff: Omit<SimulatorSession, 'id'>) => {
      if (!runInFlight) {
        openSession(handOff);
        return;
      }
      if (activeCase(session)?.id !== activeCase(handOff)?.id) {
        toast.error('A run is still in progress. It shows now; open this case once it ends.');
      }
    },
    [openSession, session, runInFlight]
  );
};
